import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Eye, Layers, Megaphone, RefreshCw, Save } from "lucide-react";

import { useStore } from "../context/StoreContext";
import { PageHeader } from "../components/bits";
import { PlatformIcon } from "../components/branding";
import { GhostBtn, INPUT, InfoTip, PrimaryBtn, Select } from "../components/ui";
import { PLATFORMS, PLATFORM_ORDER, platformName } from "../lib/platforms";
import { scheduleCount } from "../lib/format";

function assemblePreview({ base, style, docs, platformId, count, org }) {
  const system = (base || "").replaceAll("{{ORG}}", org || "the brand");
  const sorted = [...docs].sort((a, b) => (a.is_update === b.is_update ? 0 : a.is_update ? -1 : 1));
  const kb =
    sorted
      .map((d) => `## ${d.title}${d.is_update ? "  [UPDATED]" : ""}  (${d.type})\n${d.content}`)
      .join("\n\n") || "(no documents provided)";
  const updates = docs.filter((d) => d.is_update);
  const updLine = updates.length
    ? `At least ${Math.min(count, updates.length)} post(s) must centre on the most recent [UPDATED] changes.\n`
    : "";
  const pname = platformName(platformId);
  const user = `=== KNOWLEDGE BASE (everything you may reference) ===
${kb}

=== TARGET PLATFORM: ${pname} ===
Style rules:
${style}

=== TASK ===
Write ${count} distinct ${pname} posts for ${org || "the brand"}.
${updLine}Every post must reference a specific feature or update from the knowledge base (name it in "feature").
Use a different angle and a different opening for each post.

Return ONLY a JSON array of exactly ${count} objects. Do not use emojis. Do not use em dashes anywhere.`;
  return { system, user };
}

export default function PromptPage() {
  const { basePrompt, platformStyles, savePrompt, resetPrompt, documents, settings, showToast } = useStore();

  const [base, setBase] = useState(basePrompt);
  const [styles, setStyles] = useState(platformStyles);
  const [open, setOpen] = useState(PLATFORM_ORDER[0]);
  const [previewP, setPreviewP] = useState(PLATFORM_ORDER[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => setBase(basePrompt), [basePrompt]);
  useEffect(() => setStyles(platformStyles), [platformStyles]);

  const dirty =
    base !== basePrompt || PLATFORM_ORDER.some((p) => (styles[p] || "") !== (platformStyles[p] || ""));

  const assembled = useMemo(
    () =>
      assemblePreview({
        base,
        style: styles[previewP] || "",
        docs: documents,
        platformId: previewP,
        count: scheduleCount(settings.start_date, settings.end_date, settings.frequency || "weekly"),
        org: settings.org_name,
      }),
    [base, styles, previewP, documents, settings],
  );

  const save = async () => {
    setSaving(true);
    await savePrompt(base, styles);
    setSaving(false);
    showToast("Prompt saved");
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Prompt studio"
        info="This is the actual prompt remifo sends to the model, fully editable. Set a global brand voice, then refine the rules per platform so each network gets its own guidance, not one size fits all."
        right={
          <div className="flex items-center gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
            <GhostBtn onClick={resetPrompt}>
              <RefreshCw size={15} />
              Reset
            </GhostBtn>
            <PrimaryBtn onClick={save} disabled={!dirty || saving}>
              <Save size={16} />
              {saving ? "Saving" : "Save"}
            </PrimaryBtn>
          </div>
        }
      />

      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <div className="space-y-5">
          <div className="bg-surface border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Megaphone size={16} className="text-muted" />
              <h3 className="font-semibold text-foreground text-[15px]">Brand voice and rules (base)</h3>
            </div>
            <p className="text-[12.5px] text-faint mb-3">
              Applies to every post on every platform. Use{" "}
              <code className="px-1 rounded bg-foreground/5">{"{{ORG}}"}</code> for your org name.
            </p>
            <textarea
              className={`${INPUT} min-h-[280px] text-[13.5px] leading-relaxed`}
              value={base}
              onChange={(e) => setBase(e.target.value)}
            />
          </div>

          <div className="bg-surface border border-border rounded-2xl overflow-hidden">
            <div className="flex items-center gap-2 p-4 pb-3">
              <Layers size={16} className="text-muted" />
              <h3 className="font-semibold text-foreground text-[15px]">Per platform style rules</h3>
            </div>
            <div>
              {PLATFORM_ORDER.map((pid) => {
                const meta = PLATFORMS[pid];
                const expanded = open === pid;
                return (
                  <div key={pid} className="border-t border-border">
                    <button
                      onClick={() => setOpen(expanded ? null : pid)}
                      className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-foreground/5 text-left transition"
                    >
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-foreground/[0.06] text-foreground">
                        <PlatformIcon id={pid} size={15} />
                      </span>
                      <span className="font-medium text-foreground text-[14px]">{meta.name}</span>
                      <ChevronRight
                        size={16}
                        className="ml-auto text-faint transition"
                        style={{ transform: expanded ? "rotate(90deg)" : "none" }}
                      />
                    </button>
                    {expanded ? (
                      <div className="px-4 pb-4 animate-fade-in">
                        <textarea
                          className={`${INPUT} min-h-[120px] text-[13.5px] leading-relaxed`}
                          value={styles[pid] || ""}
                          onChange={(e) => setStyles((s) => ({ ...s, [pid]: e.target.value }))}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-4">
          <div className="bg-surface border border-border rounded-2xl">
            <div className="flex items-center gap-2 px-4 py-3.5 border-b border-border flex-wrap">
              <Eye size={16} className="text-muted" />
              <h3 className="font-semibold text-foreground text-[15px]">Assembled prompt preview</h3>
              <InfoTip text="This payload (system plus user message) is sent per platform when you generate. Edit on the left and it updates here. Remember to save." />
              <Select
                className="ml-auto w-44"
                buttonClassName="!bg-popover"
                value={previewP}
                onChange={setPreviewP}
                options={PLATFORM_ORDER.map((p) => ({ value: p, label: PLATFORMS[p].name }))}
              />
            </div>
            <div className="p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-faint mb-1.5">System</div>
              <pre className="text-[12px] leading-relaxed whitespace-pre-wrap text-muted bg-popover border border-border rounded-xl p-3 max-h-[220px] overflow-auto">
                {assembled.system}
              </pre>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-faint mb-1.5 mt-4">User message</div>
              <pre className="text-[12px] leading-relaxed whitespace-pre-wrap text-muted bg-popover border border-border rounded-xl p-3 max-h-[320px] overflow-auto">
                {assembled.user}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}